import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, Film, Clock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genres = [
  "Sci-Fi", "Drama", "Documentary", "Experimental", 
  "Animation", "Horror", "Comedy", "Thriller", "Action", 
  "Western", "Adventure", "Music Video", "Other"
];

export default function Submit() {
  const submissionsOpen = useSubmissionsOpen();
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    video_url: "",
    genre: "Sci-Fi",
    runtime: ""
  });

  useEffect(() => {
    fetchMySubmissions();
  }, []);

  const fetchMySubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions/my`, { withCredentials: true });
      setMySubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.video_url) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/submissions`, form, { withCredentials: true });
      toast.success("Thanks! We feature a small selection of submissions and will be in touch if your film is chosen.");
      setForm({ title: "", description: "", video_url: "", genre: "Sci-Fi", runtime: "" });
      fetchMySubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit film");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
      case "published": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected": return <XCircle className="w-4 h-4 text-slate-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "approved": return "Approved";
      // "published" used to fall through to "Pending Review", so members whose
      // film was live in the gallery were told it was still waiting.
      case "published": return "Featured";
      case "rejected": return "Not selected";
      // Deliberately not "Pending Review": that implied an answer was coming,
      // and prompted people to email asking where it was.
      default: return "Received";
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple mb-4 font-mono">
            Community Showcase
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            Submit Your Film
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Share your AI-generated films to be featured on our website. This is for community 
            showcase purposes — not a festival submission. Submit via YouTube or Vimeo. 
            Approved films will be displayed in our Films gallery.
          </p>
          <div className="mt-4 p-4 bg-purple/10 border border-purple/30 max-w-2xl">
            <p className="text-sm text-purple-light">
              <strong>Note:</strong> Looking to submit to film festivals? Check out our{" "}
              <a href="/festivals" className="underline hover:text-white">Festivals page</a>{" "}
              for upcoming AI film festivals and their submission guidelines.
            </p>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Submission Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="p-6 bg-void-surface border border-white/5">
              {submissionsOpen === null ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 text-purple animate-spin" />
                </div>
              ) : submissionsOpen === false ? (
                <div className="py-8 text-center" data-testid="submissions-paused">
                  <Clock className="w-10 h-10 text-purple-light mx-auto mb-4" />
                  <h2 className="font-syne font-bold text-white text-xl mb-3">
                    Submissions are paused
                  </h2>
                  <p className="text-slate-400">
                    We&apos;re not taking new film submissions right now. Please check back soon.
                  </p>
                </div>
              ) : (
                <>
                <h2 className="font-syne font-bold text-white text-xl mb-6 flex items-center gap-2">
                  <Send className="w-5 h-5 text-purple-light" />
                  New Submission
                </h2>
              
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Film Title *</label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Enter your film's title"
                      data-testid="submit-title"
                      className="bg-void border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Video URL *</label>
                    <Input
                      value={form.video_url}
                      onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                      placeholder="YouTube or Vimeo link"
                      data-testid="submit-url"
                      className="bg-void border-white/10 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Supported: YouTube, Vimeo
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Genre *</label>
                    <select
                      value={form.genre}
                      onChange={(e) => setForm({ ...form, genre: e.target.value })}
                      data-testid="submit-genre"
                      className="w-full px-3 py-2 bg-void border border-white/10 text-white rounded-md"
                    >
                      {genres.map((genre) => (
                        <option key={genre} value={genre}>{genre}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Runtime</label>
                    <Input
                      value={form.runtime}
                      onChange={(e) => setForm({ ...form, runtime: e.target.value })}
                      placeholder="e.g., 15 minutes"
                      data-testid="submit-runtime"
                      className="bg-void border-white/10 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Description *</label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Tell us about your film, the AI tools used, and your creative process"
                      data-testid="submit-description"
                      className="bg-void border-white/10 text-white min-h-[120px]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-btn"
                    className="w-full bg-purple hover:bg-purple/80 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Film
                      </>
                    )}
                  </Button>
                </form>
                </>
              )}
            </div>
          </motion.div>

          {/* My Submissions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="p-6 bg-void-surface border border-white/5">
              <h2 className="font-syne font-bold text-white text-xl mb-6 flex items-center gap-2">
                <Film className="w-5 h-5 text-purple-light" />
                Your Submissions
              </h2>
              <p className="text-sm text-slate-400 -mt-3 mb-6">
                We feature a small selection of submissions. If yours is chosen, we&apos;ll be in touch.
              </p>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 text-purple animate-spin" />
                </div>
              ) : mySubmissions.length === 0 ? (
                <p className="text-slate-500 text-center py-12">
                  You haven&apos;t submitted any films yet.
                </p>
              ) : (
                <div className="space-y-4">
                  {mySubmissions.map((submission) => (
                    <div
                      key={submission.id}
                      data-testid={`submission-${submission.id}`}
                      className="p-4 bg-void border border-white/5 hover:border-purple/20 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-grow">
                          <h3 className="font-medium text-white">{submission.title}</h3>
                          <p className="text-sm text-slate-500 mt-1">{submission.genre}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {getStatusIcon(submission.status)}
                          <span className={`
                            ${submission.status === 'approved' || submission.status === 'published' ? 'text-green-500' : ''}
                            ${submission.status === 'rejected' ? 'text-slate-500' : ''}
                            ${submission.status === 'pending' ? 'text-slate-400' : ''}
                          `}>
                            {getStatusText(submission.status)}
                          </span>
                        </div>
                      </div>
                      <a
                        href={submission.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-light hover:text-white mt-2 inline-block"
                      >
                        View Film →
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
